import React, { useState } from 'react';
import Modal from '../../components/ui/Modal';
import DialogShell from '../../components/ui/DialogShell';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import TextArea from '../../components/ui/TextArea';
import FormField from '../../components/ui/FormField';
import StatusBanner from '../../components/ui/StatusBanner';

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'intern', label: 'Intern' }
];

const EMPLOYEE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'on_leave', label: 'On Leave' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'terminated', label: 'Terminated' }
];

const RELOCATION_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'no', label: 'Not open to relocation' },
  { value: 'local', label: 'Local relocation' },
  { value: 'regional', label: 'Regional relocation' },
  { value: 'national', label: 'National relocation' },
  { value: 'international', label: 'International relocation' }
];

const ADDRESS_TYPE_OPTIONS = [
  { value: 'home', label: 'Home' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'prior_home', label: 'Prior home' },
  { value: 'other', label: 'Other' }
];

// Build a (changedFields, clearFields) payload from the form state.
// `clearable` is the list of field keys for which an empty string in
// the form means "explicitly clear" rather than "no change".
function diffFormState(initial, current, clearable) {
  const changed = {};
  const clearFields = [];
  for (const key of Object.keys(current)) {
    const before = initial[key] ?? '';
    const after = current[key] ?? '';
    if (String(before) === String(after)) continue;
    if (clearable.includes(key) && (after === '' || after === null)) {
      clearFields.push(key);
    } else if (after !== '' && after !== null && after !== undefined) {
      changed[key] = after;
    }
  }
  return { changed, clearFields };
}

function buildErrorsFromError(err) {
  if (!err) return { _form: '' };
  if (err.payload && Array.isArray(err.payload.issues) && err.payload.issues.length) {
    const out = {};
    for (const issue of err.payload.issues) {
      const k = issue?.path || '_form';
      if (!out[k]) out[k] = issue.message;
    }
    return out;
  }
  return { _form: err.message || 'Unable to save changes.' };
}

/* ───────────── EditEmployeeProfileModal ───────────── */

const PROFILE_CLEARABLE = [
  'preferredName', 'pronouns', 'birthDate', 'personalEmail',
  'personalPhone', 'dateOfHire', 'employmentType', 'locationId',
  'supervisorEmployeeId',
  'workEligibilityStatus', 'bio', 'careerAspirations',
  'relocationWillingness'
];

export function EditEmployeeProfileModal({ open, onClose, profile, onSave }) {
  const [form, setForm] = useState(() => ({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    preferredName: profile?.preferredName || '',
    employeeNumber: profile?.employeeNumber || '',
    status: profile?.status || 'active',
    locationId: profile?.locationId ? String(profile.locationId) : '',
    supervisorEmployeeId: profile?.supervisorEmployeeId ? String(profile.supervisorEmployeeId) : '',
    pronouns: profile?.pronouns || '',
    birthDate: profile?.birthDate || '',
    personalEmail: profile?.personalEmail || '',
    personalPhone: profile?.personalPhone || '',
    dateOfHire: profile?.dateOfHire || '',
    employmentType: profile?.employmentType || '',
    workEligibilityStatus: profile?.workEligibilityStatus || '',
    bio: profile?.bio || '',
    careerAspirations: profile?.careerAspirations || '',
    relocationWillingness: profile?.relocationWillingness || '',
    languagesSpoken: Array.isArray(profile?.languagesSpoken) ? profile.languagesSpoken.join(', ') : ''
  }));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (key) => (event) => setForm(s => ({ ...s, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    try {
      const initial = {
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        preferredName: profile?.preferredName || '',
        employeeNumber: profile?.employeeNumber || '',
        status: profile?.status || 'active',
        locationId: profile?.locationId ? String(profile.locationId) : '',
        supervisorEmployeeId: profile?.supervisorEmployeeId ? String(profile.supervisorEmployeeId) : '',
        pronouns: profile?.pronouns || '',
        birthDate: profile?.birthDate || '',
        personalEmail: profile?.personalEmail || '',
        personalPhone: profile?.personalPhone || '',
        dateOfHire: profile?.dateOfHire || '',
        employmentType: profile?.employmentType || '',
        workEligibilityStatus: profile?.workEligibilityStatus || '',
        bio: profile?.bio || '',
        careerAspirations: profile?.careerAspirations || '',
        relocationWillingness: profile?.relocationWillingness || '',
        languagesSpoken: Array.isArray(profile?.languagesSpoken) ? profile.languagesSpoken.join(', ') : ''
      };
      const { changed, clearFields } = diffFormState(initial, form, PROFILE_CLEARABLE);

      // Languages: split comma list. Empty string → empty array (an
      // explicit choice of "no recorded languages"). languagesSpoken
      // is not clearable in the contract because the array itself
      // can carry the empty state.
      if (Object.prototype.hasOwnProperty.call(changed, 'languagesSpoken') || initial.languagesSpoken !== form.languagesSpoken) {
        delete changed.languagesSpoken;
        const parsed = form.languagesSpoken
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        changed.languagesSpoken = parsed;
      }

      const payload = { ...changed };
      if (clearFields.length > 0) payload.clearFields = clearFields;

      if (Object.prototype.hasOwnProperty.call(payload, 'locationId')) {
        payload.locationId = payload.locationId === '' ? null : Number(payload.locationId);
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'supervisorEmployeeId')) {
        if (payload.supervisorEmployeeId === '') {
          delete payload.supervisorEmployeeId;
          payload.clearFields = Array.isArray(payload.clearFields) ? payload.clearFields : [];
          if (!payload.clearFields.includes('supervisorEmployeeId')) {
            payload.clearFields.push('supervisorEmployeeId');
          }
        } else {
          payload.supervisorEmployeeId = Number(payload.supervisorEmployeeId);
        }
      }

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      const saved = await onSave(payload);
      if (saved) onClose();
    } catch (err) {
      setErrors(buildErrorsFromError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      title="Edit employee profile"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting} loadingLabel="Saving…">
            Save changes
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="employee-profile-form">
        {errors._form ? <StatusBanner kind="error" message={errors._form} /> : null}

        <div className="employee-profile-form__grid">
          <FormField label="First name" htmlFor="emp-firstName" error={errors.firstName}>
            <Input id="emp-firstName" value={form.firstName} onChange={set('firstName')} />
          </FormField>
          <FormField label="Last name" htmlFor="emp-lastName" error={errors.lastName}>
            <Input id="emp-lastName" value={form.lastName} onChange={set('lastName')} />
          </FormField>
          <FormField label="Preferred name" htmlFor="emp-preferredName" error={errors.preferredName}>
            <Input id="emp-preferredName" value={form.preferredName} onChange={set('preferredName')} />
          </FormField>
          <FormField label="Employee ID" htmlFor="emp-employeeNumber" error={errors.employeeNumber}>
            <Input id="emp-employeeNumber" value={form.employeeNumber} onChange={set('employeeNumber')} />
          </FormField>
          <FormField label="Status" htmlFor="emp-status" error={errors.status}>
            <Select id="emp-status" value={form.status} onChange={set('status')}>
              {EMPLOYEE_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Location" htmlFor="emp-locationId" error={errors.locationId}>
            <Select id="emp-locationId" value={form.locationId} onChange={set('locationId')}>
              <option value="">— Select —</option>
              {(profile?.editLocations || []).map((location) => (
                <option key={location.id} value={String(location.id)}>
                  {location.code} · {location.name}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Reports to / Supervisor" htmlFor="emp-supervisorEmployeeId" error={errors.supervisorEmployeeId}>
            <Select id="emp-supervisorEmployeeId" value={form.supervisorEmployeeId} onChange={set('supervisorEmployeeId')}>
              <option value="">Unassigned</option>
              {(profile?.editSupervisorOptions || []).map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Pronouns" htmlFor="emp-pronouns" error={errors.pronouns}>
            <Input id="emp-pronouns" value={form.pronouns} onChange={set('pronouns')} />
          </FormField>
          <FormField label="Birth date" htmlFor="emp-birthDate" error={errors.birthDate} hint="YYYY-MM-DD">
            <Input id="emp-birthDate" type="date" value={form.birthDate} onChange={set('birthDate')} />
          </FormField>
          <FormField label="Personal email" htmlFor="emp-personalEmail" error={errors.personalEmail}>
            <Input id="emp-personalEmail" type="email" value={form.personalEmail} onChange={set('personalEmail')} />
          </FormField>
          <FormField label="Personal phone" htmlFor="emp-personalPhone" error={errors.personalPhone}>
            <Input id="emp-personalPhone" type="tel" value={form.personalPhone} onChange={set('personalPhone')} />
          </FormField>
          <FormField label="Hire date" htmlFor="emp-dateOfHire" error={errors.dateOfHire}>
            <Input id="emp-dateOfHire" type="date" value={form.dateOfHire} onChange={set('dateOfHire')} />
          </FormField>
          <FormField label="Employment type" htmlFor="emp-employmentType" error={errors.employmentType}>
            <Select id="emp-employmentType" value={form.employmentType} onChange={set('employmentType')}>
              {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Work eligibility" htmlFor="emp-workEligibilityStatus" error={errors.workEligibilityStatus}>
            <Input id="emp-workEligibilityStatus" value={form.workEligibilityStatus} onChange={set('workEligibilityStatus')} />
          </FormField>
          <FormField label="Relocation" htmlFor="emp-relocationWillingness" error={errors.relocationWillingness}>
            <Select id="emp-relocationWillingness" value={form.relocationWillingness} onChange={set('relocationWillingness')}>
              {RELOCATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FormField>
          <FormField
            label="Languages (comma-separated)"
            htmlFor="emp-languagesSpoken"
            error={errors.languagesSpoken}
            className="employee-profile-form__field--full"
          >
            <Input id="emp-languagesSpoken" value={form.languagesSpoken} onChange={set('languagesSpoken')} placeholder="en, es" />
          </FormField>
          <FormField label="Bio" htmlFor="emp-bio" error={errors.bio} className="employee-profile-form__field--full">
            <TextArea id="emp-bio" rows={3} value={form.bio} onChange={set('bio')} />
          </FormField>
          <FormField label="Career aspirations" htmlFor="emp-careerAspirations" error={errors.careerAspirations} className="employee-profile-form__field--full">
            <TextArea id="emp-careerAspirations" rows={3} value={form.careerAspirations} onChange={set('careerAspirations')} />
          </FormField>
        </div>

        <p className="employee-profile-form__hint">
          Clear a field by emptying its value and saving. The change is recorded in the audit log.
        </p>
      </form>
    </Modal>
  );
}

/* ───────────── AddressModal (create + edit) ───────────── */

const ADDRESS_CLEARABLE = ['line2', 'stateRegion', 'postalCode', 'effectiveFrom', 'effectiveTo'];

export function AddressModal({ open, onClose, address, onSave, presentation = 'modal' }) {
  const editing = Boolean(address);
  const [form, setForm] = useState(() => ({
    addressType: address?.addressType || 'home',
    isPrimary: address?.isPrimary || false,
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    stateRegion: address?.stateRegion || '',
    postalCode: address?.postalCode || '',
    countryCode: address?.countryCode || 'US',
    effectiveFrom: address?.effectiveFrom || '',
    effectiveTo: address?.effectiveTo || ''
  }));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(s => ({ ...s, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    try {
      if (editing) {
        const initial = {
          addressType: address.addressType,
          line1: address.line1,
          line2: address.line2 || '',
          city: address.city,
          stateRegion: address.stateRegion || '',
          postalCode: address.postalCode || '',
          countryCode: address.countryCode || 'US',
          effectiveFrom: address.effectiveFrom || '',
          effectiveTo: address.effectiveTo || ''
        };
        const current = {
          addressType: form.addressType,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          stateRegion: form.stateRegion,
          postalCode: form.postalCode,
          countryCode: form.countryCode,
          effectiveFrom: form.effectiveFrom,
          effectiveTo: form.effectiveTo
        };
        const { changed, clearFields } = diffFormState(initial, current, ADDRESS_CLEARABLE);
        const payload = { ...changed };
        if (clearFields.length > 0) payload.clearFields = clearFields;
        if (Object.keys(payload).length === 0) { onClose(); return; }
        const saved = await onSave(payload);
        if (saved) onClose();
      } else {
        const payload = {
          addressType: form.addressType,
          isPrimary: form.isPrimary,
          line1: form.line1,
          line2: form.line2 || undefined,
          city: form.city,
          stateRegion: form.stateRegion || undefined,
          postalCode: form.postalCode || undefined,
          countryCode: form.countryCode || 'US',
          effectiveFrom: form.effectiveFrom || undefined,
          effectiveTo: form.effectiveTo || undefined
        };
        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
        const saved = await onSave(payload);
        if (saved) onClose();
      }
    } catch (err) {
      setErrors(buildErrorsFromError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      title={editing ? 'Edit address' : 'Add address'}
      size="md"
      presentation={presentation}
      busy={isSubmitting}
      data-testid={presentation === 'drawer' ? 'address-drawer' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="employee-profile-form">
        {errors._form ? <StatusBanner kind="error" message={errors._form} /> : null}
        <div className="employee-profile-form__grid">
          <FormField label="Address type" htmlFor="addr-type" error={errors.addressType}>
            <Select id="addr-type" value={form.addressType} onChange={set('addressType')}>
              {ADDRESS_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </Select>
          </FormField>
          {!editing ? (
            <FormField label="Primary" htmlFor="addr-isPrimary">
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 6 }}>
                <input id="addr-isPrimary" type="checkbox" checked={form.isPrimary} onChange={set('isPrimary')} />
                Make this the primary address
              </label>
            </FormField>
          ) : <div />}
          <FormField label="Line 1" htmlFor="addr-line1" error={errors.line1} required className="employee-profile-form__field--full">
            <Input id="addr-line1" value={form.line1} onChange={set('line1')} required />
          </FormField>
          <FormField label="Line 2" htmlFor="addr-line2" error={errors.line2} className="employee-profile-form__field--full">
            <Input id="addr-line2" value={form.line2} onChange={set('line2')} />
          </FormField>
          <FormField label="City" htmlFor="addr-city" error={errors.city} required>
            <Input id="addr-city" value={form.city} onChange={set('city')} required />
          </FormField>
          <FormField label="State / region" htmlFor="addr-state" error={errors.stateRegion}>
            <Input id="addr-state" value={form.stateRegion} onChange={set('stateRegion')} />
          </FormField>
          <FormField label="Postal code" htmlFor="addr-postal" error={errors.postalCode}>
            <Input id="addr-postal" value={form.postalCode} onChange={set('postalCode')} />
          </FormField>
          <FormField label="Country (ISO-2)" htmlFor="addr-country" error={errors.countryCode}>
            <Input id="addr-country" value={form.countryCode} onChange={set('countryCode')} maxLength={2} />
          </FormField>
        </div>
      </form>
    </DialogShell>
  );
}

/* ───────────── EmergencyContactModal (create + edit) ───────────── */

const CONTACT_CLEARABLE = ['relationship', 'phonePrimary', 'phoneAlt', 'email', 'notes'];

export function EmergencyContactModal({ open, onClose, contact, onSave, presentation = 'modal' }) {
  const editing = Boolean(contact);
  const [form, setForm] = useState(() => ({
    fullName: contact?.fullName || '',
    relationship: contact?.relationship || '',
    phonePrimary: contact?.phonePrimary || '',
    phoneAlt: contact?.phoneAlt || '',
    email: contact?.email || '',
    notes: contact?.notes || '',
    displayOrder: contact?.displayOrder ?? 0
  }));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (key) => (event) => setForm(s => ({ ...s, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    try {
      if (editing) {
        const initial = {
          fullName: contact.fullName,
          relationship: contact.relationship || '',
          phonePrimary: contact.phonePrimary || '',
          phoneAlt: contact.phoneAlt || '',
          email: contact.email || '',
          notes: contact.notes || '',
          displayOrder: contact.displayOrder ?? 0
        };
        const { changed, clearFields } = diffFormState(initial, form, CONTACT_CLEARABLE);
        const payload = { ...changed };
        if (clearFields.length > 0) payload.clearFields = clearFields;
        if (Object.keys(payload).length === 0) { onClose(); return; }
        const saved = await onSave(payload);
        if (saved) onClose();
      } else {
        const payload = {
          fullName: form.fullName,
          displayOrder: Number(form.displayOrder) || 0
        };
        for (const field of CONTACT_CLEARABLE) {
          if (form[field]) payload[field] = form[field];
        }
        const saved = await onSave(payload);
        if (saved) onClose();
      }
    } catch (err) {
      setErrors(buildErrorsFromError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      title={editing ? 'Edit emergency contact' : 'Add emergency contact'}
      size="md"
      presentation={presentation}
      busy={isSubmitting}
      data-testid={presentation === 'drawer' ? 'emergency-contact-drawer' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="employee-profile-form">
        {errors._form ? <StatusBanner kind="error" message={errors._form} /> : null}
        <div className="employee-profile-form__grid">
          <FormField label="Full name" htmlFor="ec-fullName" error={errors.fullName} required className="employee-profile-form__field--full">
            <Input id="ec-fullName" value={form.fullName} onChange={set('fullName')} required />
          </FormField>
          <FormField label="Relationship" htmlFor="ec-relationship" error={errors.relationship}>
            <Input id="ec-relationship" value={form.relationship} onChange={set('relationship')} />
          </FormField>
          <FormField label="Display order" htmlFor="ec-displayOrder" error={errors.displayOrder}>
            <Input id="ec-displayOrder" type="number" min={0} value={form.displayOrder} onChange={set('displayOrder')} />
          </FormField>
          <FormField label="Phone (primary)" htmlFor="ec-phonePrimary" error={errors.phonePrimary}>
            <Input id="ec-phonePrimary" type="tel" value={form.phonePrimary} onChange={set('phonePrimary')} />
          </FormField>
          <FormField label="Phone (alt)" htmlFor="ec-phoneAlt" error={errors.phoneAlt}>
            <Input id="ec-phoneAlt" type="tel" value={form.phoneAlt} onChange={set('phoneAlt')} />
          </FormField>
          <FormField label="Email" htmlFor="ec-email" error={errors.email} className="employee-profile-form__field--full">
            <Input id="ec-email" type="email" value={form.email} onChange={set('email')} />
          </FormField>
          <FormField label="Notes" htmlFor="ec-notes" error={errors.notes} className="employee-profile-form__field--full">
            <TextArea id="ec-notes" rows={2} value={form.notes} onChange={set('notes')} />
          </FormField>
        </div>
      </form>
    </DialogShell>
  );
}

/* ───────────── PreferencesPanelEditor (inline) ───────────── */

export function PreferencesPanelEditor({ preferences, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    notificationChannelEmail: preferences?.notificationChannelEmail !== false,
    notificationChannelSms: preferences?.notificationChannelSms === true,
    notificationChannelPush: preferences?.notificationChannelPush === true,
    notificationQuietHoursStart: preferences?.notificationQuietHoursStart || '',
    notificationQuietHoursEnd: preferences?.notificationQuietHoursEnd || '',
    timezone: preferences?.timezone || 'America/Phoenix',
    displayLanguage: preferences?.displayLanguage || 'en'
  }));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setBool = (key) => (event) => setForm(s => ({ ...s, [key]: event.target.checked }));
  const setText = (key) => (event) => setForm(s => ({ ...s, [key]: event.target.value }));

  const handleSave = async () => {
    setErrors({});
    setIsSubmitting(true);
    try {
      const payload = {
        notificationChannelEmail: form.notificationChannelEmail,
        notificationChannelSms: form.notificationChannelSms,
        notificationChannelPush: form.notificationChannelPush,
        timezone: form.timezone,
        displayLanguage: form.displayLanguage
      };
      if (form.notificationQuietHoursStart) payload.notificationQuietHoursStart = form.notificationQuietHoursStart;
      if (form.notificationQuietHoursEnd) payload.notificationQuietHoursEnd = form.notificationQuietHoursEnd;

      const saved = await onSave(payload);
      if (saved) onCancel();
    } catch (err) {
      setErrors(buildErrorsFromError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="employee-profile-form" data-testid="preferences-editor">
      {errors._form ? <StatusBanner kind="error" message={errors._form} /> : null}
      <div className="employee-profile-form__grid">
        <FormField label="Channels" htmlFor="pref-channels">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 6 }}>
            <label><input type="checkbox" checked={form.notificationChannelEmail} onChange={setBool('notificationChannelEmail')} /> Email</label>
            <label><input type="checkbox" checked={form.notificationChannelSms} onChange={setBool('notificationChannelSms')} /> SMS</label>
            <label><input type="checkbox" checked={form.notificationChannelPush} onChange={setBool('notificationChannelPush')} /> Push</label>
          </div>
        </FormField>
        <FormField label="Timezone" htmlFor="pref-timezone">
          <Input id="pref-timezone" value={form.timezone} onChange={setText('timezone')} />
        </FormField>
        <FormField label="Quiet hours start" htmlFor="pref-quietStart" hint="HH:MM">
          <Input id="pref-quietStart" type="time" value={form.notificationQuietHoursStart} onChange={setText('notificationQuietHoursStart')} />
        </FormField>
        <FormField label="Quiet hours end" htmlFor="pref-quietEnd" hint="HH:MM">
          <Input id="pref-quietEnd" type="time" value={form.notificationQuietHoursEnd} onChange={setText('notificationQuietHoursEnd')} />
        </FormField>
        <FormField label="Language" htmlFor="pref-language">
          <Input id="pref-language" maxLength={2} value={form.displayLanguage} onChange={setText('displayLanguage')} />
        </FormField>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </div>
  );
}
